import {
  classUserSelectors,
  createAppSelector,
  pageGroupSelectors,
  pageGroupUserSelectors,
  pageSelectors,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import { selectClassUserIdsPresentInClass } from "./pubsub-virtual-class-state";
import {
  selectCurrentSectionUserId,
  selectEnabledSectionUserIds,
  selectEnabledSectionUsers,
  selectEnabledUserIdBySectionUserIdMap,
} from "./section-user";
import { sortByFirstName } from "./sorting";

import type { PageGroupUserType } from "components/server-types";
import { shallowRecordMemoizeOptions, stringArrayMemoizeOptions } from "store/selector-memoize";
import { compactMap, filterMap } from "utils/collections";
import { isGroupPage } from "utils/page-utils";

const selectReleasedPageIdsForCurrentSection = createAppSelector(
  [pageSelectors.filterForCurrentSection],
  (pages) =>
    filterMap(
      pages,
      (page) => !!page.released_at,
      (page) => page.id,
    ),
  stringArrayMemoizeOptions,
);

export const selectPageGroupIdsByPage = createAppSelector(
  [pageGroupSelectors.filterByPageId],
  (pageGroups) => pageGroups.map((pageGroup) => pageGroup.id),
  stringArrayMemoizeOptions,
);

const selectAllPageGroupUsersByPage = createAppSelector(
  [pageGroupUserSelectors.selectAll, selectPageGroupIdsByPage],
  (pageGroupUsers, pageGroupIds) => {
    const pageGroupIdSet = new Set(pageGroupIds);
    return pageGroupUsers.filter((pgu) => pageGroupIdSet.has(pgu.page_group_id));
  },
);

// Page group user id for a section user on the page.
// When pageGroupId is set, only that group membership; when omitted, any membership on the page
// (e.g. is_ready: false while ungrouped).
export const selectPageGroupUserIdFromSectionUser = createAppSelector(
  [
    (_state, _pageId: string, sectionUserId: string) => sectionUserId,
    (_state, _pageId: string, _sectionUserId: string, pageGroupId?: string) => pageGroupId,
    selectAllPageGroupUsersByPage,
  ],
  (sectionUserId, pageGroupId, pageGroupUsers): string | null => {
    if (pageGroupId !== undefined) {
      return (
        pageGroupUsers.find(
          (pageGroupUser) =>
            pageGroupUser.section_user_id === sectionUserId &&
            pageGroupUser.page_group_id === pageGroupId,
        )?.id ?? null
      );
    }
    return (
      pageGroupUsers.find((pageGroupUser) => pageGroupUser.section_user_id === sectionUserId)?.id ??
      null
    );
  },
);

// Page group users for the page whose section users are enabled.
// Empty unless the page is a group page — leftover memberships on non-group pages
// (e.g. template mode switches that skip cleanup) must not surface in submissions UI.
export const selectPageGroupUsersForPage = createAppSelector(
  [pageSelectors.selectById, selectAllPageGroupUsersByPage, selectEnabledSectionUserIds],
  (page, pageGroupUsers, enabledSectionUserIds) => {
    if (!page || !isGroupPage(page)) {
      return [];
    }

    const enabledSectionUserIdSet = new Set(enabledSectionUserIds);
    return pageGroupUsers.filter((pageGroupUser) =>
      enabledSectionUserIdSet.has(pageGroupUser.section_user_id),
    );
  },
);

export const selectPageGroupUserIdsForPage = createAppSelector(
  [selectPageGroupUsersForPage],
  (pageGroupUsers) => pageGroupUsers.map((pageGroupUser) => pageGroupUser.id),
  stringArrayMemoizeOptions,
);

// Section user ids with is_ready membership on a page group for this page.
export const selectReadySectionUserIdsForPage = createAppSelector(
  [selectPageGroupUsersForPage],
  (pageGroupUsers) =>
    filterMap(
      pageGroupUsers,
      (pageGroupUser) => pageGroupUser.is_ready,
      (pageGroupUser) => pageGroupUser.section_user_id,
    ),
  stringArrayMemoizeOptions,
);

// Stable fingerprint of who is in which page group on the page.
// Optional excludeSectionUserId omits that member (e.g. post-remove override stamp).
export const selectPageGroupsMembershipHashByPageId = createAppSelector(
  [
    selectPageGroupUsersForPage,
    (_state, _pageId: string, excludeSectionUserId?: string) => excludeSectionUserId,
  ],
  (pageGroupUsers, excludeSectionUserId) => {
    const membership = filterMap(
      pageGroupUsers,
      (pageGroupUser) =>
        excludeSectionUserId == null || pageGroupUser.section_user_id !== excludeSectionUserId,
      (pageGroupUser) => `${pageGroupUser.page_group_id}:${pageGroupUser.section_user_id}`,
    );
    return membership.sort().join("|");
  },
);

// True when any page group has ≥ max_group_size enabled page group users.
export const selectIsAnyPageGroupFullForPage = createAppSelector(
  [pageSelectors.selectById, selectPageGroupUsersForPage, selectPageGroupIdsByPage],
  (page, enabledPageGroupUsers, pageGroupIds) => {
    if (!page || page.max_group_size === null) {
      return false;
    }
    const maxGroupSize = page.max_group_size;
    const countByPageGroupId = new Map<string, number>();
    for (const pageGroupUser of enabledPageGroupUsers) {
      countByPageGroupId.set(
        pageGroupUser.page_group_id,
        (countByPageGroupId.get(pageGroupUser.page_group_id) ?? 0) + 1,
      );
    }
    return pageGroupIds.some(
      (pageGroupId) => (countByPageGroupId.get(pageGroupId) ?? 0) >= maxGroupSize,
    );
  },
);

export const selectPageGroupUserForCurrentUserByPageId = createAppSelector(
  [selectAllPageGroupUsersByPage, selectCurrentSectionUserId],
  (pageGroupUsers, sectionUserId) => {
    return pageGroupUsers.find((pgu) => pgu.section_user_id === sectionUserId) ?? null;
  },
);

export const selectPageGroupIdForCurrentUserByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => {
    return pageGroupUser?.page_group_id ?? null;
  },
);

// True when the current section user is ready (assigned) in a page group for the page.
export const selectIsCurrentSectionUserAssignedToPageGroupByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => !!pageGroupUser?.is_ready,
);

// Page group id for the current user when they are ready (assigned); otherwise null.
export const selectReadyPageGroupIdForCurrentUserByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => (pageGroupUser?.is_ready ? pageGroupUser.page_group_id : null),
);

// Index of a page group among page groups for the page (ordered by created_at).
// Returns -1 when pageGroupId is missing or not on the page.
export const selectPageGroupIndexByPageGroupId = createAppSelector(
  [
    pageGroupSelectors.filterByPageId,
    (_state, _pageId: string | null | undefined, pageGroupId?: string | null) => pageGroupId,
  ],
  (pageGroups, pageGroupId) => {
    if (!pageGroupId) {
      return -1;
    }
    return pageGroups.findIndex((pageGroup) => pageGroup.id === pageGroupId);
  },
);

// Page group ids that have ≥1 enabled section user on the page.
export const selectEnabledPageGroupIdsByPageId = createAppSelector(
  [pageSelectors.selectById, selectPageGroupUsersForPage],
  (page, enabledPageGroupUsers): string[] => {
    if (!page || !isGroupPage(page)) {
      return [];
    }

    return [...new Set(enabledPageGroupUsers.map((pageGroupUser) => pageGroupUser.page_group_id))];
  },
  stringArrayMemoizeOptions,
);

// Page group users for a group (enabled section users only).
export const selectPageGroupUsersByPageGroupId = createAppSelector(
  [
    (state, pageGroupId?: string | null) => pageGroupId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUserIds,
  ],
  (pageGroupId, pageGroupUsers, enabledSectionUserIds): PageGroupUserType[] => {
    if (!pageGroupId) {
      return [];
    }

    const enabledSectionUserIdSet = new Set(enabledSectionUserIds);

    return pageGroupUsers.filter(
      (pageGroupUser) =>
        pageGroupUser.page_group_id === pageGroupId &&
        enabledSectionUserIdSet.has(pageGroupUser.section_user_id),
    );
  },
);

// Class user ids that are present in class for ready members of the page group.
export const selectPresentClassUserIdsByPageGroupId = createAppSelector(
  [
    selectPageGroupUsersByPageGroupId,
    selectEnabledUserIdBySectionUserIdMap,
    classUserSelectors.filterForCurrentSection,
    selectClassUserIdsPresentInClass,
  ],
  (pageGroupUsers, userIdBySectionUserId, classUsers, presentClassUserIds): string[] => {
    const classUserIdByUserId = new Map(
      classUsers.map((classUser) => [classUser.user_id, classUser.id]),
    );
    const presentClassUserIdSet = new Set(presentClassUserIds);

    return compactMap(pageGroupUsers, (pageGroupUser) => {
      if (!pageGroupUser.is_ready) {
        return null;
      }
      const userId = userIdBySectionUserId[pageGroupUser.section_user_id];
      if (!userId) {
        return null;
      }
      const classUserId = classUserIdByUserId.get(userId);
      if (classUserId == null || !presentClassUserIdSet.has(classUserId)) {
        return null;
      }
      return classUserId;
    });
  },
  stringArrayMemoizeOptions,
);

// Same as selectPageGroupUsersByPageGroupId, sorted by first name.
export const selectSortedPageGroupUsersByPageGroupId = createAppSelector(
  [selectPageGroupUsersByPageGroupId, selectEnabledSectionUsers, userSelectors.selectEntities],
  (pageGroupUsers, enabledSectionUsers, userMap): PageGroupUserType[] =>
    pageGroupUsers.toSorted((pageGroupUserA, pageGroupUserB) => {
      const sectionUserA = enabledSectionUsers.find(
        (sectionUser) => sectionUser.id === pageGroupUserA.section_user_id,
      );
      const sectionUserB = enabledSectionUsers.find(
        (sectionUser) => sectionUser.id === pageGroupUserB.section_user_id,
      );
      const userA = sectionUserA ? userMap[sectionUserA.user_id] : null;
      const userB = sectionUserB ? userMap[sectionUserB.user_id] : null;
      if (!userA && !userB) return 0;
      if (!userA) return 1;
      if (!userB) return -1;
      return sortByFirstName(userA, userB);
    }),
);

// Ready page group users on the page (enabled section users only).
const selectReadyPageGroupUsersByPageId = createAppSelector(
  [selectPageGroupUsersForPage],
  (pageGroupUsers) => pageGroupUsers.filter((pageGroupUser) => pageGroupUser.is_ready),
);

// Ready page group users for a group, sorted by first name.
const selectSortedReadyPageGroupUsersByPageGroupId = createAppSelector(
  [selectSortedPageGroupUsersByPageGroupId],
  (pageGroupUsers) => pageGroupUsers.filter((pageGroupUser) => pageGroupUser.is_ready),
);

// Ids of ready page group users for a group (same order as sorted ready PGUs).
export const selectSortedReadyPageGroupUserIdsByPageGroupId = createAppSelector(
  [selectSortedReadyPageGroupUsersByPageGroupId],
  (pageGroupUsers) => pageGroupUsers.map((pageGroupUser) => pageGroupUser.id),
  stringArrayMemoizeOptions,
);

// User ids for ready members of a page group (same order as sorted ready PGUs).
export const selectReadyUserIdsByPageGroupId = createAppSelector(
  [selectSortedReadyPageGroupUsersByPageGroupId, selectEnabledUserIdBySectionUserIdMap],
  (pageGroupUsers, userIdBySectionUserId): string[] =>
    compactMap(
      pageGroupUsers,
      (pageGroupUser) => userIdBySectionUserId[pageGroupUser.section_user_id] ?? null,
    ),
  stringArrayMemoizeOptions,
);

// Count of page groups with no enabled page_group_users.
export const selectEmptyPageGroupCountByPageId = createAppSelector(
  [pageGroupSelectors.filterByPageId, selectEnabledPageGroupIdsByPageId],
  (pageGroups, enabledPageGroupIds) => {
    const nonEmptyPageGroupIds = new Set(enabledPageGroupIds);
    return pageGroups.filter((pageGroup) => !nonEmptyPageGroupIds.has(pageGroup.id)).length;
  },
);

export const selectPageGroupUserForUser = createAppSelector(
  [
    (state, pageId?: string | null) => pageId,
    (state, pageId?: string | null, userId?: string | null) => userId,
    selectAllPageGroupUsersByPage,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, pageGroupUsers, sectionUsers) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIdSet = new Set(
      filterMap(
        sectionUsers,
        (sectionUser) => sectionUser.user_id === userId,
        (sectionUser) => sectionUser.id,
      ),
    );

    const userPageGroupUser = pageGroupUsers.find((pgu) =>
      sectionUserIdSet.has(pgu.section_user_id),
    );

    return userPageGroupUser ?? null;
  },
);

// True when the page group already has ≥ max_group_size ready members.
export const selectIsPageGroupFull = createAppSelector(
  [
    pageSelectors.selectById,
    selectReadyPageGroupUsersByPageId,
    (_state, _pageId: string, pageGroupId?: string | null) => pageGroupId,
  ],
  (page, readyPageGroupUsers, pageGroupId) => {
    if (!pageGroupId || !page || page.max_group_size === null) {
      return false;
    }
    const readyMemberCount = readyPageGroupUsers.filter(
      (pageGroupUser) => pageGroupUser.page_group_id === pageGroupId,
    ).length;
    return readyMemberCount >= page.max_group_size;
  },
);

export const selectPageGroupForReadyUser = createAppSelector(
  [
    (state, pageId: string | null) => pageId,
    selectCurrentUserId,
    pageGroupSelectors.filterByPageId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, page_groups, page_group_users, section_users) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIdSet = new Set(
      filterMap(
        section_users,
        (section_user) => section_user.user_id === userId,
        (section_user) => section_user.id,
      ),
    );

    // Get a list of the user's page group ids
    const userGroupIdSet = new Set(
      filterMap(
        page_group_users,
        (group_user) => sectionUserIdSet.has(group_user.section_user_id) && group_user.is_ready,
        (group_user) => group_user.page_group_id,
      ),
    );

    // Go through the groups for the given page and find the group for the given user
    return page_groups.find((page_group) => userGroupIdSet.has(page_group.id));
  },
);

// Ready users in a page group, in enabled section-user order.
export const selectReadiedUsersInPageGroup = createAppSelector(
  [selectPageGroupUsersByPageGroupId, selectEnabledSectionUsers, userSelectors.selectEntities],
  (pageGroupUsers, sectionUsers, users) => {
    const readySectionUserIdSet = new Set(
      filterMap(
        pageGroupUsers,
        (pageGroupUser) => pageGroupUser.is_ready,
        (pageGroupUser) => pageGroupUser.section_user_id,
      ),
    );

    return filterMap(
      sectionUsers,
      (sectionUser) => readySectionUserIdSet.has(sectionUser.id),
      (sectionUser) => ({ ...users[sectionUser.user_id] }),
    );
  },
);

// page_id → page-group count for released pages in the current section.
export const selectGroupCountsByPageId = createAppSelector(
  [pageGroupSelectors.selectAll, selectReleasedPageIdsForCurrentSection],
  (pageGroups, releasedPageIds) => {
    const releasedPageIdSet = new Set(releasedPageIds);
    const countByPageId: Record<string, number> = {};
    for (const pageGroup of pageGroups) {
      if (releasedPageIdSet.has(pageGroup.page_id)) {
        countByPageId[pageGroup.page_id] = (countByPageId[pageGroup.page_id] ?? 0) + 1;
      }
    }
    return countByPageId;
  },
  shallowRecordMemoizeOptions,
);
